import React, { useContext } from 'react'
import {
  Image,
  ImageSourcePropType,
  Text,
  View,
  StyleSheet,
} from 'react-native'
import {
  BG_STYLES,
  COLORS,
  COMMON_STYLES,
  DefaultProductImage,
  Label,
  LazyImage,
  SIZE,
  TEXT_STYLES,
  TYPOGRAPHY,
  VARIANTS_STYLES,
} from '@dehaat/dds'
import ArrowIcon from 'app/icons/ArrowIcon'
import { CaretDownFill } from 'app/icons/CaretDownFill'
import { CaretUpFill } from 'app/icons/CaretUpFill'
import { PriceChangeRequest } from 'app/models/RequestFlow'
import { useSelector } from 'react-redux'
import { selectRoleType } from 'app/store/reducer/requestflow'
import { ResponsiveContext } from 'app/context/ResponsiveUIContext'
import { RoleType } from 'app/constants/request'

type RequestInfoProps = {
  item: PriceChangeRequest
}

const RequestInfo = ({ item }: RequestInfoProps) => {
  const roleType = useSelector(selectRoleType)
  const { mediaStyles } = useContext(ResponsiveContext)
  const calculatePercentageChange = (
    oldPrice: string,
    newPrice: string,
  ): number => {
    const oldNum = Number(oldPrice) || 0
    const newNum = Number(newPrice) || 0
    if (oldNum === 0) return 0
    return ((newNum - oldNum) / oldNum) * 100
  }
  const getStatusStyle = (status: string) => {
    const statusMap = {
      pending: {
        bgColor: 'bgYellow100' as const,
        textColor: 'textNeutral100' as const,
      },
      rejected: {
        bgColor: 'bgError100' as const,
        textColor: 'textWhite' as const,
      },
      approved: {
        bgColor: 'bgSuccess100' as const,
        textColor: 'textWhite' as const,
      },
    }

    for (const [key, value] of Object.entries(statusMap)) {
      if (status?.toLowerCase().includes(key)) {
        return value
      }
    }
    return {
      bgColor: 'bgNeutral20' as const,
      textColor: 'textNeutral100' as const,
    }
  }
  const percentageChange = calculatePercentageChange(
    item.old_price,
    item.suggested_price,
  )
  const isDecrease = percentageChange < 0
  const isNewProduct = Number(item.old_price) === 0
  return (
    <>
      <View style={styles.topSection}>
        <View style={styles.productInfo}>
          <LazyImage
            imageUrl={item.product_variant.product_image}
            fallBackIcon={
              <Image
                source={DefaultProductImage as ImageSourcePropType}
                style={styles.productImage}
              />
            }
            imgStyles={styles.productImage}
          />

          <View style={styles.productDetails}>
            <Text style={[styles.brandText]} numberOfLines={0}>
              {item.product_variant.brand}
            </Text>
            <Text style={styles.productName}>
              {item.product_variant.product_name}
            </Text>
            <Text style={styles.productName}>
              {item.product_variant.attributes_name}
            </Text>
          </View>
        </View>

        <View style={styles.statusSection}>
          <Label
            text="Price Change Request"
            bgColor="bgNeutral20"
            textColor="textNeutral90"
            size={SIZE.small}
            containerStyle={styles.requestTypeLabel}
          />
          <View style={[COMMON_STYLES['mb0.5'], styles.statusRow]}>
            <Text style={styles.statusLabel}>Status:</Text>
            <Label
              text={item.latest_status.replace(/_/g, ' ')}
              {...getStatusStyle(item.latest_status)}
              size={SIZE.small}
              containerStyle={styles.statusBadge}
            />
          </View>
        </View>
      </View>
      <View style={styles.priceWrapper}>
        <View style={styles.priceSection}>
          <View style={styles.priceColumn}>
            <Text style={styles.priceLabel}>Old Selling Price</Text>
            <Text style={styles.priceValue}>₹{item.old_price}</Text>
          </View>

          <View style={styles.priceColumn}>
            <ArrowIcon />
            <View style={styles.percentageContainer}>
              <Text
                style={[
                  styles.percentageText,
                  mediaStyles({
                    desktopStyle: {
                      ...TYPOGRAPHY.CAPTION1,
                    },
                  }),
                  isDecrease ? styles.decreaseText : styles.increaseText,
                ]}
              >
                {isNewProduct
                  ? 'NEW'
                  : `${Math.abs(percentageChange).toFixed(1)}%`}
              </Text>
              {!isNewProduct &&
                (isDecrease ? (
                  <CaretDownFill
                    width={16}
                    height={10}
                    color={COLORS.error100}
                  />
                ) : (
                  <CaretUpFill
                    width={16}
                    height={10}
                    color={COLORS.success100}
                  />
                ))}
            </View>
          </View>
          <View style={styles.priceColumn}>
            <Text style={styles.priceLabel}>New Selling Price</Text>
            <Text style={styles.priceValue}>₹{item.suggested_price}</Text>
          </View>
        </View>
        {(roleType === RoleType.L1 || roleType === RoleType.L2) && (
          <View
            style={[
              BG_STYLES.bgNeutral20,
              {
                paddingVertical: 2,
                borderBottomRightRadius: 8,
                borderBottomLeftRadius: 8,
              },
            ]}
          >
            <Text
              style={[
                styles.threshText,
                mediaStyles({
                  desktopStyle: {
                    ...TYPOGRAPHY.CAPTION1,
                  },
                }),
              ]}
            >
              Threshold Price: {item?.threshold_price || 'NaN'}
            </Text>
          </View>
        )}
      </View>
    </>
  )
}
const styles = StyleSheet.create({
  topSection: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.mb1,
  },
  productInfo: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.flex1,
  },
  productImage: {
    width: 64,
    height: 64,
    ...COMMON_STYLES['mr3/4'],
  },
  productDetails: {
    ...COMMON_STYLES.justifyCenter,
    gap: 3,
    flexShrink: 1,
    flexGrow: 1,
    minWidth: 0,
  },
  brandText: {
    ...TYPOGRAPHY.CAPTION1,
    ...VARIANTS_STYLES.MEDIUM,
    ...TEXT_STYLES.textNeutral70,
    flexWrap: 'wrap',
  },
  productName: {
    ...TYPOGRAPHY.TEXT1,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textNeutral90,
    flexWrap: 'wrap',
  },
  statusSection: {
    ...COMMON_STYLES.alignItemsEnd,
    minWidth: 140,
  },
  requestTypeLabel: {
    ...COMMON_STYLES['ph1/2'],
    ...COMMON_STYLES['pv1/4'],
    ...COMMON_STYLES['mb0.5'],
  },
  statusRow: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    gap: 6,
  },
  statusLabel: {
    ...TYPOGRAPHY.CAPTION2,
    ...VARIANTS_STYLES.BASE,
    ...TEXT_STYLES.textNeutral70,
  },
  statusBadge: {
    ...COMMON_STYLES['ph1/2'],
    ...COMMON_STYLES['pv1/4'],
  },
  priceSection: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    ...COMMON_STYLES.justifyBetween,
    ...COMMON_STYLES['p0.5'],
  },
  priceWrapper: {
    ...BG_STYLES.bgNeutral10,
    ...COMMON_STYLES['br1/2'],
  },
  threshText: {
    ...TYPOGRAPHY.CAPTION2,
    ...VARIANTS_STYLES.BASE,
    ...TEXT_STYLES.textNeutral70,
    ...COMMON_STYLES.textCenter,
  },
  priceColumn: {
    ...COMMON_STYLES.alignItemsCenter,
    ...COMMON_STYLES.flex1,
    gap: 4,
  },
  priceLabel: {
    ...TYPOGRAPHY.CAPTION2,
    ...VARIANTS_STYLES.BASE,
    ...TEXT_STYLES.textNeutral70,
  },
  priceValue: {
    ...TYPOGRAPHY.TEXT2,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
    ...TEXT_STYLES.textNeutral90,
  },
  percentageText: {
    ...TYPOGRAPHY.CAPTION2,
    ...VARIANTS_STYLES.SEMI_EMPHASIZED,
  },
  decreaseText: {
    ...TEXT_STYLES.textError100,
  },
  increaseText: {
    ...TEXT_STYLES.textSuccess100,
  },
  percentageContainer: {
    ...COMMON_STYLES.row,
    ...COMMON_STYLES.alignItemsCenter,
    gap: 2,
  },
})
export default RequestInfo
